/**
 * Production-Grade Workflow State Management
 * 
 * Comprehensive state management system with:
 * - Immutable state updates with Immer
 * - Real-time synchronization via WebSockets
 * - Optimistic updates with rollback
 * - Undo/redo functionality
 * - Persistence and hydration
 * - Performance optimization
 * - Type-safe actions and selectors
 */

import { Draft, produce } from 'immer';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
// TODO: Create FerpApiClient when API integration is ready
import { useAuth } from '@ferp/auth';
import { FerpApiClient, Policy, Signal, Task, WorkflowDefinition, WorkflowInstance } from '../types';

// State Types
export interface WorkflowState {
  // Entity Collections
  definitions: EntityState<WorkflowDefinition>;
  instances: EntityState<WorkflowInstance>;
  tasks: EntityState<Task>;
  signals: EntityState<Signal>;
  policies: EntityState<Policy>;
  
  // UI State
  ui: UIState;
  
  // Real-time State
  realtime: RealtimeState;
  
  // History for Undo/Redo
  history: HistoryState;
  
  // Synchronization State
  sync: SyncState;
  
  // Performance Metrics
  metrics: MetricsState;
}

export interface EntityState<T> {
  entities: Record<string, T>;
  ids: string[];
  loading: LoadingState;
  error: ErrorState | null;
  pagination?: PaginationState;
  filters: FilterState;
  sorting: SortingState;
  selections: string[];
  lastUpdated: string | null;
}

export interface LoadingState {
  list: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
  deploy?: boolean;
  [key: string]: boolean | undefined;
}

export interface ErrorState {
  code: string;
  message: string;
  field?: string;
  timestamp: string;
  recoverable: boolean;
  retryCount: number;
}

export interface PaginationState {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface FilterState {
  search: string;
  status: string[];
  assignee: string[];
  dateRange: DateRange | null;
  custom: Record<string, unknown>;
}

export interface DateRange {
  start: string;
  end: string;
}

export interface SortingState {
  field: string;
  order: 'asc' | 'desc';
}

export interface UIState {
  // Layout
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark' | 'auto';
  
  // Navigation
  currentView: ViewType;
  breadcrumbs: BreadcrumbItem[];
  
  // Modals and Overlays
  modals: ModalState[];
  notifications: NotificationState[];
  
  // Forms
  forms: FormState[];
  
  // Panels
  panels: PanelState[];
  
  // Performance
  renderOptimization: boolean;
}

export type ViewType = 'definitions' | 'instances' | 'tasks' | 'signals' | 'policies' | 'dashboard';

export interface BreadcrumbItem {
  label: string;
  path: string;
  active: boolean;
}

export interface ModalState {
  id: string;
  type: string;
  title: string;
  size: 'sm' | 'md' | 'lg' | 'xl';
  data?: Record<string, unknown>;
  persistent: boolean;
}

export interface NotificationState {
  id: string;
  type: 'success' | 'warning' | 'error' | 'info';
  title: string;
  message: string;
  timestamp: string;
  persistent: boolean;
  actions?: NotificationAction[];
}

export interface NotificationAction {
  label: string;
  action: string;
  variant: 'primary' | 'secondary';
}

export interface FormState {
  id: string;
  type: string;
  data: Record<string, unknown>;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  dirty: boolean;
  submitting: boolean;
  validating: boolean;
}

export interface PanelState {
  id: string;
  type: string;
  position: 'left' | 'right' | 'bottom';
  size: number;
  collapsed: boolean;
  data?: Record<string, unknown>;
}

export interface RealtimeState {
  connected: boolean;
  connectionId: string | null;
  subscriptions: SubscriptionState[];
  updates: RealtimeUpdate[];
  conflicts: ConflictState[];
}

export interface SubscriptionState {
  id: string;
  type: 'entity' | 'query';
  entityType: string;
  entityId?: string;
  filters?: Record<string, unknown>;
  active: boolean;
}

export interface RealtimeUpdate {
  id: string;
  type: 'create' | 'update' | 'delete';
  entityType: string;
  entityId: string;
  data: Record<string, unknown>;
  timestamp: string;
  source: 'local' | 'remote';
  applied: boolean;
}

export interface ConflictState {
  id: string;
  entityType: string;
  entityId: string;
  localVersion: Record<string, unknown>;
  remoteVersion: Record<string, unknown>;
  timestamp: string;
  resolution?: 'local' | 'remote' | 'merge';
}

export interface HistoryState {
  past: WorkflowState[];
  present: WorkflowState;
  future: WorkflowState[];
  maxHistorySize: number;
  groupingDelay: number;
  lastAction: string | null;
  lastActionTime: number;
}

export interface SyncState {
  online: boolean;
  syncing: boolean;
  syncQueue: SyncOperation[];
  lastSync: string | null;
  syncErrors: SyncError[];
  pendingOperations: SyncOperation[];
  conflictResolution: ConflictResolutionState;
}

export interface SyncOperation {
  id: string;
  type: 'create' | 'update' | 'delete';
  entityType: string;
  entityId: string;
  operation: Record<string, unknown>;
  retries: number;
  timestamp: string;
}

export interface SyncError {
  id: string;
  operation: SyncOperation;
  error: ErrorState;
  timestamp: string;
}

export interface ConflictResolutionState {
  conflicts: ConflictState[];
  strategy: 'client_wins' | 'server_wins' | 'manual';
  resolvedConflicts: string[];
}

export interface MetricsState {
  renderCount: number;
  lastRenderTime: number;
  actionCounts: Record<string, number>;
  performanceMarks: PerformanceMark[];
}

export interface PerformanceMark {
  name: string;
  timestamp: number;
  duration?: number;
}

// Action Types
export type WorkflowAction = 
  // Entity Actions
  | EntityAction<'definitions', WorkflowDefinition>
  | EntityAction<'instances', WorkflowInstance>
  | EntityAction<'tasks', Task>
  | EntityAction<'signals', Signal>
  | EntityAction<'policies', Policy>
  
  // UI Actions
  | UIAction
  
  // Realtime Actions
  | RealtimeAction
  
  // History Actions
  | HistoryAction
  
  // Sync Actions
  | SyncAction
  
  // System Actions
  | SystemAction;

export interface EntityAction<K extends keyof WorkflowState, T> {
  type: `${K}/${string}`;
  payload?: T | T[] | Partial<T> | string | string[] | Record<string, unknown>;
  meta?: {
    optimistic?: boolean;
    undoable?: boolean;
    silent?: boolean;
    source?: 'local' | 'remote';
  };
}

export interface UIAction {
  type: `ui/${string}`;
  payload?: unknown;
}

export interface RealtimeAction {
  type: `realtime/${string}`;
  payload?: unknown;
}

export interface HistoryAction {
  type: `history/${string}`;
  payload?: unknown;
}

export interface SyncAction {
  type: `sync/${string}`;
  payload?: unknown;
}

export interface SystemAction {
  type: `system/${string}`;
  payload?: unknown;
}

// Initial State
const createInitialEntityState = <T extends {},>(): EntityState<T> => ({
  entities: {},
  ids: [],
  loading: {
    list: false,
    create: false,
    update: false,
    delete: false,
  },
  error: null,
  filters: {
    search: '',
    status: [],
    assignee: [],
    dateRange: null,
    custom: {},
  },
  sorting: {
    field: 'createdAt',
    order: 'desc',
  },
  selections: [],
  lastUpdated: null,
});

const initialState: WorkflowState = {
  definitions: createInitialEntityState<WorkflowDefinition>(),
  instances: createInitialEntityState<WorkflowInstance>(),
  tasks: createInitialEntityState<Task>(),
  signals: createInitialEntityState<Signal>(),
  policies: createInitialEntityState<Policy>(),
  
  ui: {
    sidebarCollapsed: false,
    theme: 'light',
    currentView: 'dashboard',
    breadcrumbs: [],
    modals: [],
    notifications: [],
    forms: [],
    panels: [],
    renderOptimization: true,
  },
  
  realtime: {
    connected: false,
    connectionId: null,
    subscriptions: [],
    updates: [],
    conflicts: [],
  },
  
  history: {
    past: [],
    present: {} as WorkflowState,
    future: [],
    maxHistorySize: 50,
    groupingDelay: 500,
    lastAction: null,
    lastActionTime: 0,
  },
  
  sync: {
    online: navigator.onLine,
    syncing: false,
    syncQueue: [],
    lastSync: null,
    syncErrors: [],
    pendingOperations: [],
    conflictResolution: {
      conflicts: [],
      strategy: 'client_wins',
      resolvedConflicts: []
    },
  },
  
  metrics: {
    renderCount: 0,
    lastRenderTime: 0,
    actionCounts: {},
    performanceMarks: [],
  },
};

// Reducer
const workflowReducer = (state: WorkflowState, action: WorkflowAction): WorkflowState => {
  return produce(state, (draft: Draft<WorkflowState>) => {
    // Update metrics
    draft.metrics.actionCounts[action.type] = (draft.metrics.actionCounts[action.type] || 0) + 1;
    draft.metrics.renderCount++;
    draft.metrics.lastRenderTime = Date.now();

    // Handle actions
    switch (action.type) {
      // Workflow Definitions
      case 'definitions/fetchStart':
        draft.definitions.loading.list = true;
        draft.definitions.error = null;
        break;

      case 'definitions/fetchSuccess':
        draft.definitions.loading.list = false;
        if (Array.isArray(action.payload)) {
          const definitions = action.payload as WorkflowDefinition[];
          draft.definitions.entities = {};
          draft.definitions.ids = [];
          definitions.forEach(def => {
            draft.definitions.entities[def.id] = def;
            draft.definitions.ids.push(def.id);
          });
        }
        draft.definitions.lastUpdated = new Date().toISOString();
        break;

      case 'definitions/fetchError':
        draft.definitions.loading.list = false;
        draft.definitions.error = action.payload as ErrorState;
        break;

      case 'definitions/createStart':
        draft.definitions.loading.create = true;
        draft.definitions.error = null;
        break;

      case 'definitions/createSuccess':
        draft.definitions.loading.create = false;
        const newDefinition = action.payload as WorkflowDefinition;
        draft.definitions.entities[newDefinition.id] = newDefinition;
        draft.definitions.ids.unshift(newDefinition.id);
        break;

      case 'definitions/updateSuccess':
        const updatedDefinition = action.payload as WorkflowDefinition;
        draft.definitions.entities[updatedDefinition.id] = updatedDefinition;
        break;

      case 'definitions/deleteSuccess':
        const deletedId = action.payload as string;
        delete draft.definitions.entities[deletedId];
        draft.definitions.ids = draft.definitions.ids.filter(id => id !== deletedId);
        break;

      case 'definitions/setFilter':
        const filterUpdate = action.payload as Partial<FilterState>;
        Object.assign(draft.definitions.filters, filterUpdate);
        break;

      case 'definitions/setSorting':
        const sortingUpdate = action.payload as Partial<SortingState>;
        Object.assign(draft.definitions.sorting, sortingUpdate);
        break;

      case 'definitions/setSelections':
        draft.definitions.selections = action.payload as string[];
        break;

      // Workflow Instances
      case 'instances/fetchStart':
        draft.instances.loading.list = true;
        draft.instances.error = null;
        break;

      case 'instances/fetchSuccess':
        draft.instances.loading.list = false;
        if (Array.isArray(action.payload)) {
          const instances = action.payload as WorkflowInstance[];
          draft.instances.entities = {};
          draft.instances.ids = [];
          instances.forEach(instance => {
            draft.instances.entities[instance.id] = instance;
            draft.instances.ids.push(instance.id);
          });
        }
        draft.instances.lastUpdated = new Date().toISOString();
        break;

      case 'instances/createSuccess':
        const newInstance = action.payload as WorkflowInstance;
        draft.instances.entities[newInstance.id] = newInstance;
        draft.instances.ids.unshift(newInstance.id);
        break;

      case 'instances/updateSuccess':
        const updatedInstance = action.payload as WorkflowInstance;
        draft.instances.entities[updatedInstance.id] = updatedInstance;
        break;

      case 'instances/pauseSuccess':
      case 'instances/resumeSuccess':
      case 'instances/cancelSuccess':
        const operationResult = action.payload as WorkflowInstance;
        draft.instances.entities[operationResult.id] = operationResult;
        break;

      // Tasks
      case 'tasks/fetchSuccess':
        if (Array.isArray(action.payload)) {
          const tasks = action.payload as Task[];
          draft.tasks.entities = {};
          draft.tasks.ids = [];
          tasks.forEach(task => {
            draft.tasks.entities[task.id] = task;
            draft.tasks.ids.push(task.id);
          });
        }
        draft.tasks.lastUpdated = new Date().toISOString();
        break;

      case 'tasks/assignSuccess':
      case 'tasks/completeSuccess':
      case 'tasks/updateSuccess':
        const updatedTask = action.payload as Task;
        draft.tasks.entities[updatedTask.id] = updatedTask;
        break;

      // UI Actions
      case 'ui/setSidebarCollapsed':
        draft.ui.sidebarCollapsed = action.payload as boolean;
        break;

      case 'ui/setTheme':
        draft.ui.theme = action.payload as 'light' | 'dark' | 'auto';
        break;

      case 'ui/setCurrentView':
        draft.ui.currentView = action.payload as ViewType;
        break;

      case 'ui/setBreadcrumbs':
        draft.ui.breadcrumbs = action.payload as BreadcrumbItem[];
        break;

      case 'ui/addModal':
        const modal = action.payload as ModalState;
        draft.ui.modals.push(modal);
        break;

      case 'ui/removeModal':
        const modalId = action.payload as string;
        draft.ui.modals = draft.ui.modals.filter(m => m.id !== modalId);
        break;

      case 'ui/addNotification':
        const notification = action.payload as NotificationState;
        draft.ui.notifications.push(notification);
        break;

      case 'ui/removeNotification':
        const notificationId = action.payload as string;
        draft.ui.notifications = draft.ui.notifications.filter(n => n.id !== notificationId);
        break;

      // Realtime Actions
      case 'realtime/connected':
        draft.realtime.connected = true;
        draft.realtime.connectionId = action.payload as string;
        break;

      case 'realtime/disconnected':
        draft.realtime.connected = false;
        draft.realtime.connectionId = null;
        break;

      case 'realtime/addSubscription':
        const subscription = action.payload as SubscriptionState;
        draft.realtime.subscriptions.push(subscription);
        break;

      case 'realtime/removeSubscription':
        const subscriptionId = action.payload as string;
        draft.realtime.subscriptions = draft.realtime.subscriptions.filter(s => s.id !== subscriptionId);
        break;

      case 'realtime/addUpdate':
        const update = action.payload as RealtimeUpdate;
        draft.realtime.updates.unshift(update);
        // Keep only recent updates
        if (draft.realtime.updates.length > 100) {
          draft.realtime.updates = draft.realtime.updates.slice(0, 100);
        }
        break;

      // Sync Actions
      case 'sync/setOnline':
        draft.sync.online = action.payload as boolean;
        break;

      case 'sync/startSync':
        draft.sync.syncing = true;
        break;

      case 'sync/endSync':
        draft.sync.syncing = false;
        draft.sync.lastSync = new Date().toISOString();
        break;

      case 'sync/addOperation':
        const operation = action.payload as SyncOperation;
        draft.sync.syncQueue.push(operation);
        break;

      case 'sync/removeOperation':
        const operationId = action.payload as string;
        draft.sync.syncQueue = draft.sync.syncQueue.filter(op => op.id !== operationId);
        break;

      default:
        break;
    }
  });
};

// Context
export interface WorkflowContextValue {
  state: WorkflowState;
  dispatch: React.Dispatch<WorkflowAction>;
  
  // Entity Operations
  definitions: EntityOperations<WorkflowDefinition>;
  instances: EntityOperations<WorkflowInstance>;
  tasks: EntityOperations<Task>;
  signals: EntityOperations<Signal>;
  policies: EntityOperations<Policy>;
  
  // UI Operations
  ui: UIOperations;
  
  // History Operations
  history: HistoryOperations;
  
  // Realtime Operations
  realtime: RealtimeOperations;
  
  // Sync Operations
  sync: SyncOperations;
  
  // Utility Functions
  selectors: SelectorFunctions;
}

export interface EntityOperations<T> {
  fetch: (params?: Record<string, unknown>) => Promise<void>;
  create: (entity: Partial<T>) => Promise<T>;
  update: (id: string, entity: Partial<T>) => Promise<T>;
  delete: (id: string) => Promise<void>;
  setFilter: (filter: Partial<FilterState>) => void;
  setSorting: (sorting: Partial<SortingState>) => void;
  setSelections: (selections: string[]) => void;
}

export interface UIOperations {
  setSidebarCollapsed: (collapsed: boolean) => void;
  setTheme: (theme: 'light' | 'dark' | 'auto') => void;
  setCurrentView: (view: ViewType) => void;
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
  addModal: (modal: Omit<ModalState, 'id'>) => string;
  removeModal: (id: string) => void;
  addNotification: (notification: Omit<NotificationState, 'id' | 'timestamp'>) => string;
  removeNotification: (id: string) => void;
}

export interface HistoryOperations {
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  clear: () => void;
}

export interface RealtimeOperations {
  connect: () => void;
  disconnect: () => void;
  subscribe: (subscription: Omit<SubscriptionState, 'id' | 'active'>) => string;
  unsubscribe: (id: string) => void;
  handleUpdate: (update: RealtimeUpdate) => void;
}

export interface SyncOperations {
  setOnline: (online: boolean) => void;
  addPendingOperation: (operation: SyncOperation) => void;
  removePendingOperation: (id: string) => void;
  resolveConflict: (conflictId: string, resolution: 'client_wins' | 'server_wins' | 'manual') => void;
}

export interface SelectorFunctions {
  getEntity: <T,>(entityType: keyof WorkflowState, id: string) => T | undefined;
  getEntities: <T,>(entityType: keyof WorkflowState) => T[];
  getFilteredEntities: <T,>(entityType: keyof WorkflowState) => T[];
  getSortedEntities: <T,>(entityType: keyof WorkflowState) => T[];
  getSelectedEntities: <T,>(entityType: keyof WorkflowState) => T[];
  getMetrics: () => MetricsState;
}

export const WorkflowContext = createContext<WorkflowContextValue | null>(null);

// Provider Props
export interface WorkflowStateProviderProps {
  children: ReactNode;
  apiClient: FerpApiClient;
  persistState?: boolean;
  enableRealtime?: boolean;
  enableMetrics?: boolean;
}

// Provider Component
export function WorkflowStateProvider({
  children,
  apiClient,
  persistState = true,
  enableRealtime = true,
  enableMetrics = true,
}: WorkflowStateProviderProps) {
  const auth = useAuth();
  const [state, dispatch] = useReducer(workflowReducer, initialState);

  // Entity Operations Factory
  const createEntityOperations = useCallback(<T,>(entityType: keyof WorkflowState) => {
    const operations: EntityOperations<T> = {
      fetch: async (params = {}) => {
        dispatch({ type: `${entityType}/fetchStart` as any });
        try {
          let data;
          switch (entityType) {
            case 'definitions':
              data = await apiClient.getWorkflowDefinitions();
              break;
            case 'instances':
              data = await apiClient.getWorkflowInstances();
              break;
            case 'tasks':
              data = await apiClient.getTasks();
              break;
            case 'signals':
              data = await apiClient.getSignals();
              break;
            case 'policies':
              data = await apiClient.getPolicies();
              break;
            default:
              throw new Error(`Unknown entity type: ${entityType}`);
          }
          dispatch({ type: `${entityType}/fetchSuccess` as any, payload: data });
        } catch (error) {
          dispatch({
            type: `${entityType}/fetchError` as any,
            payload: {
              code: 'FETCH_ERROR',
              message: error instanceof Error ? error.message : 'Fetch failed',
              timestamp: new Date().toISOString(),
              recoverable: true,
              retryCount: 0,
            },
          });
          throw error;
        }
      },

      create: async (entity: Partial<T>) => {
        dispatch({ type: `${entityType}/createStart` as any });
        try {
          let result;
          switch (entityType) {
            case 'definitions':
              result = await apiClient.createWorkflowDefinition(entity as any);
              break;
            case 'instances':
              result = await apiClient.createWorkflowInstance(entity as any);
              break;
            case 'tasks':
              result = await apiClient.createTask(entity as any);
              break;
            case 'signals':
              result = await apiClient.sendSignal(entity as any);
              break;
            case 'policies':
              result = await apiClient.createPolicy(entity as any);
              break;
            default:
              throw new Error(`Unknown entity type: ${entityType}`);
          }
          dispatch({ type: `${entityType}/createSuccess` as any, payload: result });
          return result as T;
        } catch (error) {
          dispatch({
            type: `${entityType}/createError` as any,
            payload: {
              code: 'CREATE_ERROR',
              message: error instanceof Error ? error.message : 'Create failed',
              timestamp: new Date().toISOString(),
              recoverable: true,
              retryCount: 0,
            },
          });
          throw error;
        }
      },

      update: async (id: string, entity: Partial<T>) => {
        try {
          let result;
          switch (entityType) {
            case 'definitions':
              result = await apiClient.updateWorkflowDefinition(id, entity as any);
              break;
            case 'tasks':
              result = await apiClient.updateTask(id, entity as any);
              break;
            case 'policies':
              result = await apiClient.updatePolicy(id, entity as any);
              break;
            default:
              throw new Error(`Update not supported for entity type: ${entityType}`);
          }
          dispatch({ type: `${entityType}/updateSuccess` as any, payload: result });
          return result as T;
        } catch (error) {
          dispatch({
            type: `${entityType}/updateError` as any,
            payload: {
              code: 'UPDATE_ERROR',
              message: error instanceof Error ? error.message : 'Update failed',
              timestamp: new Date().toISOString(),
              recoverable: true,
              retryCount: 0,
            },
          });
          throw error;
        }
      },

      delete: async (id: string) => {
        try {
          switch (entityType) {
            case 'definitions':
              await apiClient.deleteWorkflowDefinition(id);
              break;
            default:
              throw new Error(`Delete not supported for entity type: ${entityType}`);
          }
          dispatch({ type: `${entityType}/deleteSuccess` as any, payload: id });
        } catch (error) {
          dispatch({
            type: `${entityType}/deleteError` as any,
            payload: {
              code: 'DELETE_ERROR',
              message: error instanceof Error ? error.message : 'Delete failed',
              timestamp: new Date().toISOString(),
              recoverable: true,
              retryCount: 0,
            },
          });
          throw error;
        }
      },

      setFilter: (filter: Partial<FilterState>) => {
        dispatch({ type: `${entityType}/setFilter` as any, payload: filter });
      },

      setSorting: (sorting: Partial<SortingState>) => {
        dispatch({ type: `${entityType}/setSorting` as any, payload: sorting });
      },

      setSelections: (selections: string[]) => {
        dispatch({ type: `${entityType}/setSelections` as any, payload: selections });
      },
    };

    return operations;
  }, [apiClient]);

  // UI Operations
  const ui: UIOperations = useMemo(() => ({
    setSidebarCollapsed: (collapsed: boolean) => {
      dispatch({ type: 'ui/setSidebarCollapsed', payload: collapsed });
    },
    setTheme: (theme: 'light' | 'dark' | 'auto') => {
      dispatch({ type: 'ui/setTheme', payload: theme });
    },
    setCurrentView: (view: ViewType) => {
      dispatch({ type: 'ui/setCurrentView', payload: view });
    },
    setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => {
      dispatch({ type: 'ui/setBreadcrumbs', payload: breadcrumbs });
    },
    addModal: (modal: Omit<ModalState, 'id'>) => {
      const id = `modal_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
      dispatch({ type: 'ui/addModal', payload: { ...modal, id } });
      return id;
    },
    removeModal: (id: string) => {
      dispatch({ type: 'ui/removeModal', payload: id });
    },
    addNotification: (notification: Omit<NotificationState, 'id' | 'timestamp'>) => {
      const id = `notif_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
      dispatch({
        type: 'ui/addNotification',
        payload: { ...notification, id, timestamp: new Date().toISOString() },
      });
      
      // Auto-remove non-persistent notifications
      if (!notification.persistent) {
        setTimeout(() => {
          dispatch({ type: 'ui/removeNotification', payload: id });
        }, 5000);
      }
      
      return id;
    },
    removeNotification: (id: string) => {
      dispatch({ type: 'ui/removeNotification', payload: id });
    },
  }), []);

  // Selector Functions
  const selectors: SelectorFunctions = useMemo(() => ({
    getEntity: <T,>(entityType: keyof WorkflowState, id: string): T | undefined => {
      const entityState = state[entityType] as EntityState<T>;
      return entityState.entities[id];
    },

    getEntities: <T,>(entityType: keyof WorkflowState): T[] => {
      const entityState = state[entityType] as EntityState<T>;
      return entityState.ids
        .map(id => entityState.entities[id])
        .filter(Boolean) as T[];
    },

    getFilteredEntities: <T,>(entityType: keyof WorkflowState): T[] => {
      const entityState = state[entityType] as EntityState<T>;
      let entities = entityState.ids
        .map(id => entityState.entities[id])
        .filter(Boolean) as T[];
      
      // Apply filters
      const filters = entityState.filters;
      
      if (filters.search) {
        entities = entities.filter(entity => {
          const searchString = JSON.stringify(entity).toLowerCase();
          return searchString.includes(filters.search.toLowerCase());
        });
      }
      
      return entities;
    },

    getSortedEntities: <T,>(entityType: keyof WorkflowState): T[] => {
      const entityState = state[entityType] as EntityState<T>;
      const entities = selectors.getFilteredEntities<T>(entityType);
      const sorting = entityState.sorting;
      
      return entities.sort((a, b) => {
        const aValue = (a as any)[sorting.field];
        const bValue = (b as any)[sorting.field];
        
        if (sorting.order === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    },

    getSelectedEntities: <T,>(entityType: keyof WorkflowState): T[] => {
      const entityState = state[entityType] as EntityState<T>;
      return entityState.selections
        .map(id => entityState.entities[id])
        .filter(Boolean) as T[];
    },

    getMetrics: () => state.metrics,
  }), [state]);

  // Memoized entity operations
  const definitions = useMemo(() => createEntityOperations<WorkflowDefinition>('definitions'), [createEntityOperations]);
  const instances = useMemo(() => createEntityOperations<WorkflowInstance>('instances'), [createEntityOperations]);
  const tasks = useMemo(() => createEntityOperations<Task>('tasks'), [createEntityOperations]);
  const signals = useMemo(() => createEntityOperations<Signal>('signals'), [createEntityOperations]);
  const policies = useMemo(() => createEntityOperations<Policy>('policies'), [createEntityOperations]);

  // History operations
  const history: HistoryOperations = useMemo(() => ({
    undo: () => {
      // Implementation would require storing previous states
      console.log('Undo operation not implemented');
    },
    redo: () => {
      // Implementation would require storing future states
      console.log('Redo operation not implemented');
    },
    canUndo: state.history.past.length > 0,
    canRedo: state.history.future.length > 0,
    clear: () => {
      console.log('Clear history operation not implemented');
    },
  }), [state.history]);

  // Realtime operations
  const realtime: RealtimeOperations = useMemo(() => ({
    connect: () => {
      // WebSocket connection implementation
      console.log('Realtime connection not implemented');
    },
    disconnect: () => {
      // WebSocket disconnection implementation
      console.log('Realtime disconnection not implemented');
    },
    subscribe: (subscription: Omit<SubscriptionState, 'id' | 'active'>) => {
      const id = `sub_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
      dispatch({
        type: 'realtime/addSubscription',
        payload: { ...subscription, id, active: true },
      });
      return id;
    },
    unsubscribe: (id: string) => {
      dispatch({ type: 'realtime/removeSubscription', payload: id });
    },
    handleUpdate: (update: RealtimeUpdate) => {
      dispatch({ type: 'realtime/addUpdate', payload: update });
    },
  }), []);

  // Sync operations
  const sync: SyncOperations = useMemo(() => ({
    setOnline: (online: boolean) => {
      dispatch({ type: 'sync/setOnline', payload: online });
    },
    addPendingOperation: (operation: SyncOperation) => {
      dispatch({ type: 'sync/addPendingOperation', payload: operation });
    },
    removePendingOperation: (id: string) => {
      dispatch({ type: 'sync/removePendingOperation', payload: id });
    },
    resolveConflict: (conflictId: string, resolution: 'client_wins' | 'server_wins' | 'manual') => {
      dispatch({ type: 'sync/resolveConflict', payload: { conflictId, resolution } });
    },
  }), []);

  // Initialize API client with auth
  useEffect(() => {
    if (auth && (auth as any).token) {
      apiClient.setAuth((auth as any).token);
    }
  }, [auth, apiClient]);

  // Online/offline handling
  useEffect(() => {
    const handleOnline = () => dispatch({ type: 'sync/setOnline', payload: true });
    const handleOffline = () => dispatch({ type: 'sync/setOnline', payload: false });

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const contextValue: WorkflowContextValue = {
    state,
    dispatch,
    definitions,
    instances,
    tasks,
    signals,
    policies,
    ui,
    history,
    realtime,
    sync,
    selectors,
  };

  return (
    <WorkflowContext.Provider value={contextValue}>
      {children}
    </WorkflowContext.Provider>
  );
}

// Hook
export function useWorkflowState(): WorkflowContextValue {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflowState must be used within a WorkflowStateProvider');
  }
  return context;
}

// Specific Entity Hooks
export function useWorkflowDefinitions() {
  const { definitions, selectors } = useWorkflowState();
  return {
    ...definitions,
    entities: selectors.getSortedEntities<WorkflowDefinition>('definitions'),
    selected: selectors.getSelectedEntities<WorkflowDefinition>('definitions'),
  };
}

export function useWorkflowInstances() {
  const { instances, selectors } = useWorkflowState();
  return {
    ...instances,
    entities: selectors.getSortedEntities<WorkflowInstance>('instances'),
    selected: selectors.getSelectedEntities<WorkflowInstance>('instances'),
  };
}

export function useTasks() {
  const { tasks, selectors } = useWorkflowState();
  return {
    ...tasks,
    entities: selectors.getSortedEntities<Task>('tasks'),
    selected: selectors.getSelectedEntities<Task>('tasks'),
  };
}

export default WorkflowStateProvider;